import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Breadcrumb,
  Tag,
  Card,
  Row,
  Col,
  message,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HomeOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import { panelsService, Panel } from '@/renderer/services/panelsService'
import './PanelsList.css'

const PANEL_STATUSES = [
  { label: 'Available (AVLB)', value: 'AVLB', color: 'green' },
  { label: 'In Use (NUSE)', value: 'NUSE', color: 'blue' },
  { label: 'Inspection (INSP)', value: 'INSP', color: 'orange' },
  { label: 'Maintenance (MTCE)', value: 'MTCE', color: 'orange' },
  { label: 'Repair (REPR)', value: 'REPR', color: 'red' },
  { label: 'Quarantined (QRTD)', value: 'QRTD', color: 'red' },
  { label: 'Retired (RTRD)', value: 'RTRD', color: 'purple' },
]

const PANEL_TYPES = [
  { label: 'Data Acquisition Panel (DAP)', value: 'dap' },
  { label: 'Conditional Panel', value: 'conditional' },
  { label: 'Winch Control Panel', value: 'winch' },
]

const COMPANY_LOCATIONS = [
  { label: 'Neuquén - Operaciones', value: 'Neuquén - Operaciones' },
  { label: 'Neuquén - Base', value: 'Neuquén - Base' },
  { label: 'Comodoro - Operaciones', value: 'Comodoro - Operaciones' },
  { label: 'Comodoro - Base', value: 'Comodoro - Base' },
  { label: 'Buenos Aires - Office', value: 'Buenos Aires - Office' },
]

interface ExtendedPanel extends Panel {
  key: string
}

export function PanelsList() {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [panels, setPanels] = useState<ExtendedPanel[]>([])
  const [loading, setLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingPanel, setEditingPanel] = useState<ExtendedPanel | null>(null)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 })
  const [filters, setFilters] = useState({ status: '', location: '', panel_type: '' })

  useEffect(() => {
    loadPanels()
  }, [pagination.current, filters])

  const loadPanels = async () => {
    setLoading(true)
    try {
      const skip = (pagination.current - 1) * pagination.pageSize
      const response = await panelsService.listPanels(skip, pagination.pageSize, filters)
      const panelsWithKeys: ExtendedPanel[] = response.items.map((panel) => ({
        ...panel,
        key: panel.id,
      }))
      setPanels(panelsWithKeys)
      setPagination({ ...pagination, total: response.total })
    } catch (error) {
      message.error('Failed to load panels')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    form.resetFields()
    setEditingPanel(null)
    setIsModalVisible(true)
  }

  const handleEdit = (panel: ExtendedPanel) => {
    form.setFieldsValue(panel)
    setEditingPanel(panel)
    setIsModalVisible(true)
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      if (editingPanel) {
        await panelsService.updatePanel(editingPanel.id, values)
        message.success('Panel updated successfully')
      } else {
        await panelsService.createPanel(values)
        message.success('Panel created successfully')
      }
      setIsModalVisible(false)
      setPagination({ ...pagination, current: 1 })
      await loadPanels()
    } catch (error) {
      console.error('Error saving panel:', error)
      message.error('Failed to save panel')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await panelsService.deletePanel(id)
      message.success('Panel deleted successfully')
      await loadPanels()
    } catch (error) {
      console.error('Error deleting panel:', error)
      message.error('Failed to delete panel')
    }
  }

  const columns: ColumnsType<ExtendedPanel> = [
    {
      title: 'Panel Type',
      dataIndex: 'panel_type',
      key: 'panel_type',
      width: 150,
    },
    {
      title: 'Key/S/N',
      dataIndex: 'key_or_sn',
      key: 'key_or_sn',
      width: 150,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusConfig = PANEL_STATUSES.find((s) => s.value === status)
        return statusConfig ? <Tag color={statusConfig.color}>{statusConfig.label}</Tag> : status
      },
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 150,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
          <Popconfirm
            title="Delete Panel"
            description="Are you sure you want to delete this panel?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumb style={{ marginBottom: '16px' }}>
        <Breadcrumb.Item>
          <a onClick={() => navigate('/inventory')}>
            <HomeOutlined />
          </a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Inventory</Breadcrumb.Item>
        <Breadcrumb.Item>Panels</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 600 }}>Panels Management</h1>
        <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
          Manage electronic panels (DAP, Conditional, Winch control)
        </p>
      </div>

      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space wrap>
              <Select
                placeholder="Filter by Status"
                style={{ width: 180 }}
                allowClear
                options={PANEL_STATUSES}
                onChange={(value) => {
                  setFilters({ ...filters, status: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
              <Select
                placeholder="Filter by Location"
                style={{ width: 180 }}
                allowClear
                options={COMPANY_LOCATIONS}
                onChange={(value) => {
                  setFilters({ ...filters, location: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
              <Select
                placeholder="Filter by Panel Type"
                style={{ width: 180 }}
                allowClear
                options={PANEL_TYPES}
                onChange={(value) => {
                  setFilters({ ...filters, panel_type: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
            </Space>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Panel
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={panels}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (page) => setPagination({ ...pagination, current: page }),
        }}
        size="small"
      />

      <Modal
        title={editingPanel ? 'Edit Panel' : 'Add Panel'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Panel Type"
            name="panel_type"
            rules={[{ required: true, message: 'Panel Type is required' }]}
          >
            <Select placeholder="Select panel type" options={PANEL_TYPES} />
          </Form.Item>

          <Form.Item
            label="Key or Serial Number"
            name="key_or_sn"
            rules={[{ required: true, message: 'Key or S/N is required' }]}
          >
            <Input placeholder="Key or serial number" />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Status is required' }]}
          >
            <Select placeholder="Select status" options={PANEL_STATUSES} />
          </Form.Item>

          <Form.Item
            label="Location"
            name="location"
            rules={[{ required: true, message: 'Location is required' }]}
          >
            <Select placeholder="Select location" options={COMPANY_LOCATIONS} />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea placeholder="Additional description" rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
